import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  Calendar,
  User,
  Wrench,
  DollarSign,
  Plus,
  HelpCircle,
  Mail,
  Users,
  UserCog,
  Folder,
  MapPin,
  Headphones,
  TrendingUp,
  Bell,
  Bug,
  Settings,
  ChevronDown,
  LogOut,
  Phone,
  Play,
  Receipt,
  Menu,
  X,
  Gift,
  ShieldAlert,
  CalendarCheck,
  CalendarDays,
  Send,
  Building2,
  ClipboardList,
  MessageSquare,
  Image,
  Bot,
  BookOpen,
  RefreshCw
} from 'lucide-react';
import Dashboard from './Dashboard';
import AppointmentsPage from './AppointmentsPage';
import AppointmentDetailPage from './AppointmentDetailPage';
import TechniciansPage from './TechniciansPage';
import TechnicianDetailPage from './TechnicianDetailPage';
import ClientsPage from './ClientsPage';
import ClientDetailPage from './ClientDetailPage';
import PayrollPage from './PayrollPage';
import HiringPage from './HiringPage';
import RequestsPage from './RequestsPage';
import ServicesPage from './ServicesPage';
import LocationsPage from './LocationsPage';
import LocationDetailPage from './LocationDetailPage';
import OutreachPage from './OutreachPage';
import LeadsPage from './LeadsPage';
import LeadDetailPage from './LeadDetailPage';
import QualityPage from './QualityPage';
import MetricsPage from './MetricsPage';
import TrackingPage from './TrackingPage';
import LoginPage from './LoginPage';
import SettingsPage from './SettingsPage';
import MakeCallTestPage from './MakeCallTestPage';
import CallsPage from './CallsPage';
import CoveragePage from './CoveragePage';
import FinancePage from './FinancePage';
import GiftCardsPage from './GiftCardsPage';
import OnlineBookingsPage from './OnlineBookingsPage';
import JobsPage from './JobsPage';
import DataQualityPage from './DataQualityPage';
import PhotoGalleryPage from './PhotoGalleryPage';
import PhoneForwardingPage from './PhoneForwardingPage';
import GoogleAdsPage from './GoogleAdsPage';
import GBPPage from './GBPPage';
import TasksPage from './TasksPage';
import CommsPage from './CommsPage';
import UsersPage from './UsersPage';
import CampaignsPage from './CampaignsPage';
import SubscribersPage from './SubscribersPage';
import RebookPage from './RebookPage';
import CalendarPage from './CalendarPage';
import BookingPage from './BookingPage';
import OliviaPage from './OliviaPage';
import PlaybookPage from './PlaybookPage';
import DedupPage from './DedupPage';
import AlfPage from './AlfPage';
import MessagingPage from './MessagingPage';
import apiService from './services/api';
import GlobalSearch from './components/GlobalSearch';
import { GitSyncStatus, CallModal, BookingAssistant } from './components';

function RestrictedPage() {
  return (
    <div className="flex-1 flex items-center justify-center bg-neutral-50">
      <div className="text-center p-8 max-w-md">
        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <ShieldAlert className="w-6 h-6 text-gray-400" />
        </div>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Page Not Available</h2>
        <p className="text-sm text-gray-500">This page is not part of your role. Contact your admin if you need access.</p>
      </div>
    </div>
  );
}

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = React.useState(null);
  const [showUserMenu, setShowUserMenu] = React.useState(false);
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [newRequestCount, setNewRequestCount] = React.useState(0);
  const [leadsFollowUpCount, setLeadsFollowUpCount] = React.useState(0);
  const [existingClientCount, setExistingClientCount] = React.useState(0);
  const [unmatchedTxCount, setUnmatchedTxCount] = React.useState(0);
  const [pendingBookingCount, setPendingBookingCount] = React.useState(0);
  const [unreadMessageCount, setUnreadMessageCount] = React.useState(0);
  const [showNotifications, setShowNotifications] = React.useState(false);
  const [showDialer, setShowDialer] = React.useState(false);
  const [dialerQuery, setDialerQuery] = React.useState('');
  const [dialerResults, setDialerResults] = React.useState([]);
  const [dialerSearching, setDialerSearching] = React.useState(false);
  const [callTarget, setCallTarget] = React.useState(null);
  const [showBookingAssistant, setShowBookingAssistant] = React.useState(false);
  const dialerTimeout = React.useRef(null);

  React.useEffect(() => {
    // Fetch user profile on mount
    const fetchUser = async () => {
      try {
        const userData = await apiService.get('/auth/profile/');
        setUser(userData);
        if (userData?.role) localStorage.setItem('user_role', userData.role);
      } catch (err) {
        console.error('Error fetching user:', err);
      }
    };
    fetchUser();
  }, []);

  // Close sidebar on route change (mobile)
  React.useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Fetch new request count on mount and every 60 seconds
  React.useEffect(() => {
    const isCoordinator = user?.role === 'coordinator';
    const fetchNewCount = async () => {
      try {
        const response = await apiService.getInteractions({ page_size: 1, status: 'new', exclude_internal: 'true' });
        setNewRequestCount(response.status_counts?.new || response.count || 0);
        setExistingClientCount(response.status_counts?.existing_client_request || 0);
      } catch (err) {
        // silently ignore
      }
      if (!isCoordinator) {
        try {
          const txResponse = await apiService.get('/amn/transactions/unmatched/', { page_size: 1 });
          setUnmatchedTxCount(txResponse.count || 0);
        } catch (err) {
          // silently ignore
        }
        try {
          const bookingStats = await apiService.getOnlineBookingStats();
          setPendingBookingCount(bookingStats.paid_count || 0);
        } catch (err) {
          // silently ignore
        }
      }
      try {
        const unreadData = await apiService.getUnreadCounts();
        setUnreadMessageCount(unreadData.total_unread || 0);
      } catch (err) {
        // silently ignore
      }
      try {
        const leadsData = await apiService.get('/amn/leads/followup-count/');
        setLeadsFollowUpCount(leadsData.count || 0);
      } catch (err) {
        // silently ignore
      }
    };
    fetchNewCount();
    const interval = setInterval(fetchNewCount, 60000);
    return () => clearInterval(interval);
  }, [user?.role]);

  const handleDialerSearch = (query) => {
    setDialerQuery(query);
    if (dialerTimeout.current) clearTimeout(dialerTimeout.current);
    if (!query.trim()) { setDialerResults([]); return; }
    dialerTimeout.current = setTimeout(async () => {
      setDialerSearching(true);
      try {
        const data = await apiService.get('/amn/persons/', { search: query, page_size: 8 });
        setDialerResults(data.results || []);
      } catch (err) { /* silent */ }
      finally { setDialerSearching(false); }
    }, 300);
  };

  const startCall = (phone, name, personId) => {
    setCallTarget({ phone, name, personId });
    setShowDialer(false);
    setDialerQuery('');
    setDialerResults([]);
  };

  const formatDialerPhone = (phone) => {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    if (digits.length >= 10) {
      const last10 = digits.slice(-10);
      return `(${last10.slice(0,3)}) ${last10.slice(3,6)}-${last10.slice(6)}`;
    }
    return phone;
  };

  const handleLogout = () => {
    apiService.logout();
  };

  const getActiveMenu = () => {
    switch (location.pathname) {
      case '/appointments':
        return 'appointments';
      case '/calendar':
        return 'calendar';
      case '/alf':
        return 'alf';
      case '/technicians':
        return 'technicians';
      case '/clients':
        return 'clients';
      case '/payroll':
        return 'payroll';
      case '/hiring':
        return 'hiring';
      case '/requests':
        return 'requests';
      case '/services':
        return 'services';
      case '/locations':
        return 'locations';
      case '/outreach':
        return 'outreach';
      case '/leads':
        return 'leads';
      case '/rebook':
        return 'rebook';
      case '/tasks':
        return 'tasks';
      case '/comms':
        return 'comms';
      case '/quality':
        return 'quality';
      case '/metrics':
        return 'metrics';
      case '/tracking':
        return 'tracking';
      case '/jobs':
        return 'jobs';
      case '/finance':
        return 'finance';
      case '/gift-cards':
        return 'gift-cards';
      case '/online-bookings':
        return 'online-bookings';
      case '/campaigns':
        return 'campaigns';
      case '/messages':
        return 'messages';
      case '/new-booking':
        return 'new-booking';
      case '/olivia':
        return 'olivia';
      case '/settings':
        return 'settings';
      case '/users':
        return 'users';
      case '/gbp':
        return 'gbp';
      default:
        return 'home';
    }
  };

  const adminOnly = new Set(['tracking', 'jobs', 'test-call', 'data-quality', 'users']);
  const comingSoon = new Set(['outreach']);
  // Coordinator (Client Care) — only client-facing pages
  // Coordinator (Client Care) — only client-facing pages, no dashboard/financials
  const coordinatorAllowed = new Set([
    'appointments', 'calendar', 'comms', 'clients',
    'services', 'locations', 'requests', 'online-bookings', 'coverage', 'technicians', 'messages', 'phone-forwarding',
    'leads', 'rebook', 'playbook',
  ]);

  const allMenuItems = [
    // Operations
    { id: 'home', icon: Home, text: 'Home', path: '/' },
    { id: 'new-booking', icon: CalendarCheck, text: 'New Booking', path: '/new-booking' },
    { id: 'appointments', icon: Calendar, text: 'Appointments', path: '/appointments' },
    { id: 'calendar', icon: CalendarDays, text: 'Calendar', path: '/calendar' },
    { id: 'clients', icon: User, text: 'Clients', path: '/clients' },
    { id: 'online-bookings', icon: CalendarCheck, text: 'Online Bookings', path: '/online-bookings' },
    { id: 'comms', icon: ShieldAlert, text: 'CC Board', path: '/comms' },
    { id: 'requests', icon: HelpCircle, text: 'Requests', path: '/requests' },
    { id: 'messages', icon: MessageSquare, text: 'Messages', path: '/messages' },
    { id: 'calls', icon: Phone, text: 'Outgoing Calls', path: '/calls' },
    { id: 'alf', icon: Building2, text: 'ALF', path: '/alf' },
    { id: 'coverage', icon: MapPin, text: 'Coverage', path: '/coverage' },
    { id: 'phone-forwarding', icon: Phone, text: 'Phone Forwarding', path: '/phone-forwarding' },
    // Sales
    { id: '_divider_sales', divider: true, label: 'Sales' },
    { id: 'leads', icon: Users, text: 'Leads', path: '/leads' },
    { id: 'rebook', icon: RefreshCw, text: 'Rebook', path: '/rebook' },
    { id: 'olivia', icon: Bot, text: 'Olivia AI', path: '/olivia' },
    { id: 'playbook', icon: BookOpen, text: 'Playbook', path: '/playbook' },
    // Marketing
    { id: '_divider_marketing', divider: true, label: 'Marketing' },
    { id: 'campaigns', icon: Send, text: 'Campaigns', path: '/campaigns' },
    { id: 'subscribers', icon: Users, text: 'Subscribers', path: '/subscribers' },
    { id: 'outreach', icon: Mail, text: 'Outreach', path: '/outreach' },
    { id: 'google-ads', icon: TrendingUp, text: 'Google Ads', path: '/google-ads' },
    { id: 'gbp', icon: MapPin, text: 'GBP', path: '/gbp' },
    // Finance
    { id: '_divider_finance', divider: true, label: 'Finance' },
    { id: 'finance', icon: Receipt, text: 'Finance', path: '/finance' },
    { id: 'payroll', icon: DollarSign, text: 'Team Payout', path: '/payroll' },
    { id: 'gift-cards', icon: Gift, text: 'Gift Cards', path: '/gift-cards' },
    // Team & Business
    { id: '_divider_team', divider: true, label: 'Team' },
    { id: 'technicians', icon: Wrench, text: 'Team Members', path: '/technicians' },
    { id: 'services', icon: Folder, text: 'Services', path: '/services' },
    { id: 'locations', icon: MapPin, text: 'Locations', path: '/locations' },
    { id: 'hiring', icon: Plus, text: 'Hiring', path: '/hiring' },
    { id: 'tasks', icon: ClipboardList, text: 'Tasks', path: '/tasks' },
    { id: 'photos', icon: Image, text: 'Photos', path: '/photos' },
    { id: 'quality', icon: Headphones, text: 'Quality', path: '/quality' },
    // Analytics & Admin
    { id: '_divider_analytics', divider: true, label: 'Analytics' },
    { id: 'metrics', icon: TrendingUp, text: 'Metrics', path: '/metrics' },
    { id: 'tracking', icon: Bug, text: 'Tracking', path: '/tracking' },
    { id: 'data-quality', icon: ShieldAlert, text: 'Data Quality', path: '/data-quality' },
    { id: 'duplicates', icon: Users, text: 'Dedup Review', path: '/data-quality/duplicates' },
    { id: 'jobs', icon: Play, text: 'Jobs', path: '/jobs' },
    { id: 'test-call', icon: Phone, text: 'Test Call', path: '/test-call' },
    { id: 'users', icon: UserCog, text: 'Users', path: '/users' }
  ];

  const operatorExtra = new Set(['calls']);
  const filterWithDividers = (items, allowed) => {
    const filtered = items.filter(item => item.divider || allowed.has(item.id));
    return filtered.filter((item, i) => {
      if (!item.divider) return true;
      const next = filtered[i + 1];
      return next && !next.divider;
    });
  };

  const menuItems = user?.role === 'admin'
    ? allMenuItems
    : user?.role === 'coordinator'
      ? filterWithDividers(allMenuItems, coordinatorAllowed)
      : user?.role === 'operator'
        ? allMenuItems.filter(item => !adminOnly.has(item.id) || operatorExtra.has(item.id))
        : allMenuItems.filter(item => !adminOnly.has(item.id) && !operatorExtra.has(item.id));

  const activeMenu = getActiveMenu();

  // Block technicians from accessing the dashboard
  if (user?.role === 'technician') {
    return (
      <div className="flex h-screen bg-neutral-50 items-center justify-center" style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}>
        <div className="text-center p-8 max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShieldAlert className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Access Restricted</h1>
          <p className="text-gray-600 mb-6">
            The dashboard is not available for team member accounts. Please use the Appointment Completion Form to submit your reviews.
          </p>
          <a
            href="https://www.amourmobilenails.com/appointment-completion-form/"
            className="inline-block bg-red-700 text-white px-6 py-3 rounded-md font-medium hover:bg-red-800 transition-colors"
          >
            Go to Completion Form
          </a>
          <button
            onClick={handleLogout}
            className="block mx-auto mt-4 text-sm text-gray-500 hover:text-gray-700"
          >
            Logout
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-neutral-50" style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}>
      {/* Mobile Sidebar Backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Left Sidebar - Hidden on mobile, shown as overlay when toggled */}
      <div className={`
        fixed md:relative inset-y-0 left-0 z-50
        w-64 md:w-52 bg-white border-r border-gray-200 flex flex-col
        transform transition-transform duration-200 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `}>
        {/* Logo + Close button on mobile */}
        <div className="p-3 flex justify-between items-center border-b border-gray-200">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="Logo" className="w-10 h-10 object-contain" />
            <span className="text-sm font-semibold text-gray-800 md:hidden">AMN</span>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="p-2 rounded-lg hover:bg-gray-100 md:hidden"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Navigation Menu */}
        <div className="flex-1 p-2 overflow-y-auto">
          <nav className="space-y-0.5">
            {menuItems.map((item, index) => {
              if (item.divider) {
                return (
                  <div key={index} className="pt-3 pb-1 px-2">
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{item.label}</p>
                  </div>
                );
              }
              const isSoon = user?.role !== 'admin' && comingSoon.has(item.id);
              return (
                <div
                  key={index}
                  className={`flex items-center space-x-2 py-2 md:py-1.5 px-3 md:px-2 rounded transition-colors ${
                    isSoon
                      ? 'text-gray-300 cursor-default border border-transparent'
                      : item.id === activeMenu
                        ? 'bg-red-50 text-red-800 border border-red-200 cursor-pointer'
                        : 'text-gray-600 hover:bg-gray-50 border border-transparent cursor-pointer'
                  }`}
                  onClick={() => !isSoon && item.path && navigate(item.path)}
                >
                  <item.icon className={`w-5 h-5 md:w-4 md:h-4 ${isSoon ? 'text-gray-300' : item.id === activeMenu ? 'text-red-800' : 'text-gray-400'}`} />
                  <span className="text-sm font-medium flex-1">{item.text}{isSoon ? ' (soon)' : ''}</span>
                  {item.id === 'messages' && unreadMessageCount > 0 && (
                    <span className="px-1.5 py-0.5 text-[10px] font-bold bg-red-600 text-white rounded-full min-w-[18px] text-center">
                      {unreadMessageCount}
                    </span>
                  )}
                </div>
              );
            })}
          </nav>
        </div>

        {/* Settings - pinned to bottom */}
        <div className="p-2 border-t border-gray-200">
          <div
            className={`flex items-center space-x-2 py-2 md:py-1.5 px-3 md:px-2 rounded cursor-pointer transition-colors ${
              activeMenu === 'settings'
                ? 'bg-red-50 text-red-800 border border-red-200'
                : 'text-gray-600 hover:bg-gray-50 border border-transparent'
            }`}
            onClick={() => navigate('/settings')}
          >
            <Settings className={`w-5 h-5 md:w-4 md:h-4 ${activeMenu === 'settings' ? 'text-red-800' : 'text-gray-400'}`} />
            <span className="text-sm font-medium">Settings</span>
          </div>
        </div>

        {/* Git Sync Status - Bottom of sidebar */}
        {user?.role === 'admin' && (
          <div className="p-2 border-t border-gray-200">
            <GitSyncStatus />
          </div>
        )}
        <div className="px-3 py-1 text-center">
          <span className="text-xs text-gray-500">v2.5.0 · {process.env.REACT_APP_BUILD_TIME || 'dev'}</span>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Header Bar */}
        <div className="h-14 md:h-12 bg-white border-b border-gray-200 flex items-center justify-between px-3 gap-2">
          {/* Hamburger Menu - Mobile only */}
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 -ml-1 rounded-lg hover:bg-gray-100 md:hidden"
          >
            <Menu className="w-5 h-5 text-gray-600" />
          </button>

          {/* Global Search - Hidden on small mobile */}
          <div className="hidden sm:block flex-1 max-w-md">
            <GlobalSearch navigate={navigate} />
          </div>

          {/* Mobile: Show logo in center when search is hidden */}
          <div className="flex-1 flex justify-center sm:hidden">
            <img src="/logo.png" alt="Logo" className="w-8 h-8 object-contain" />
          </div>

          {/* User Profile */}
          <div className="flex items-center space-x-2 md:space-x-3">
            {/* Booking Assistant */}
            <button
              onClick={() => navigate('/new-booking')}
              className="relative p-1 rounded hover:bg-gray-100 transition-colors"
              title="New booking assistant"
            >
              <CalendarCheck className="w-5 h-5 md:w-4 md:h-4 text-red-800 hover:text-red-900" />
            </button>

            {/* Quick Dialer */}
            <div className="relative">
              <button
                onClick={() => { setShowDialer(!showDialer); setShowNotifications(false); }}
                className="relative p-1 rounded hover:bg-gray-100 transition-colors"
                title="Quick dial"
              >
                <Phone className="w-5 h-5 md:w-4 md:h-4 text-green-600 hover:text-green-800" />
              </button>

              {showDialer && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowDialer(false)} />
                  <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
                    <div className="p-3 border-b border-gray-100">
                      <input
                        type="text"
                        value={dialerQuery}
                        onChange={(e) => handleDialerSearch(e.target.value)}
                        placeholder="Search contact or enter number..."
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && dialerQuery.trim() && dialerResults.length === 0) {
                            const digits = dialerQuery.replace(/\D/g, '');
                            if (digits.length >= 10) {
                              startCall(dialerQuery, null, null);
                            }
                          }
                        }}
                      />
                    </div>
                    <div className="max-h-60 overflow-y-auto">
                      {dialerSearching ? (
                        <div className="px-4 py-3 text-sm text-gray-400 text-center">Searching...</div>
                      ) : dialerResults.length > 0 ? (
                        dialerResults.map((person) => (
                          <button
                            key={person.id}
                            onClick={() => startCall(
                              person.phone_number,
                              `${person.first_name || ''} ${person.last_name || ''}`.trim(),
                              person.id
                            )}
                            className="w-full px-4 py-2.5 text-left hover:bg-gray-50 border-b border-gray-100 last:border-0 transition-colors"
                            disabled={!person.phone_number}
                          >
                            <p className="text-sm font-medium text-gray-900">
                              {person.first_name || ''} {person.last_name || ''}
                            </p>
                            <p className="text-xs text-gray-500">
                              {person.phone_number ? formatDialerPhone(person.phone_number) : 'No phone number'}
                            </p>
                          </button>
                        ))
                      ) : dialerQuery.trim() ? (
                        <div className="px-4 py-3">
                          <p className="text-xs text-gray-400 text-center mb-2">No contacts found</p>
                          {dialerQuery.replace(/\D/g, '').length >= 10 && (
                            <button
                              onClick={() => startCall(dialerQuery, null, null)}
                              className="w-full px-3 py-2 text-sm bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors font-medium"
                            >
                              Call {formatDialerPhone(dialerQuery)}
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="px-4 py-3 text-xs text-gray-400 text-center">
                          Type a name or phone number
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="relative">
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-1 rounded hover:bg-gray-100 transition-colors"
              >
                <Bell className="w-5 h-5 md:w-4 md:h-4 text-gray-400 hover:text-gray-600" />
                {(newRequestCount + leadsFollowUpCount + existingClientCount + unmatchedTxCount + pendingBookingCount) > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                    {newRequestCount + leadsFollowUpCount + existingClientCount + unmatchedTxCount + pendingBookingCount}
                  </span>
                )}
              </button>

              {showNotifications && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowNotifications(false)}
                  />
                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-20">
                    <div className="px-4 py-2 border-b border-gray-100">
                      <p className="text-sm font-semibold text-gray-900">Notifications</p>
                    </div>
                    {(newRequestCount > 0 || leadsFollowUpCount > 0 || existingClientCount > 0 || unmatchedTxCount > 0 || pendingBookingCount > 0) ? (
                      <>
                        {newRequestCount > 0 && (
                          <button
                            onClick={() => {
                              setShowNotifications(false);
                              navigate('/requests');
                            }}
                            className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                          >
                            <div className="flex items-center space-x-3">
                              <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                                <Mail className="w-4 h-4 text-red-600" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">{newRequestCount} new request{newRequestCount !== 1 ? 's' : ''}</p>
                                <p className="text-xs text-gray-500">Click to view</p>
                              </div>
                            </div>
                          </button>
                        )}
                        {leadsFollowUpCount > 0 && (
                          <button
                            onClick={() => {
                              setShowNotifications(false);
                              navigate('/leads');
                            }}
                            className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                          >
                            <div className="flex items-center space-x-3">
                              <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                                <Users className="w-4 h-4 text-amber-600" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">{leadsFollowUpCount} lead{leadsFollowUpCount !== 1 ? 's' : ''} to follow up</p>
                                <p className="text-xs text-gray-500">Click to view</p>
                              </div>
                            </div>
                          </button>
                        )}
                        {existingClientCount > 0 && (
                          <button
                            onClick={() => {
                              setShowNotifications(false);
                              navigate('/requests?status=existing_client_request');
                            }}
                            className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                          >
                            <div className="flex items-center space-x-3">
                              <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                                <Phone className="w-4 h-4 text-teal-600" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">{existingClientCount} existing client request{existingClientCount !== 1 ? 's' : ''}</p>
                                <p className="text-xs text-gray-500">Click to view</p>
                              </div>
                            </div>
                          </button>
                        )}
                        {unmatchedTxCount > 0 && user?.role !== 'coordinator' && (
                          <button
                            onClick={() => {
                              setShowNotifications(false);
                              navigate('/finance?unmatched=true');
                            }}
                            className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                          >
                            <div className="flex items-center space-x-3">
                              <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                                <DollarSign className="w-4 h-4 text-orange-600" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">{unmatchedTxCount} unlinked transaction{unmatchedTxCount !== 1 ? 's' : ''}</p>
                                <p className="text-xs text-gray-500">Click to view</p>
                              </div>
                            </div>
                          </button>
                        )}
                        {pendingBookingCount > 0 && user?.role !== 'coordinator' && (
                          <button
                            onClick={() => {
                              setShowNotifications(false);
                              navigate('/online-bookings?status=paid');
                            }}
                            className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                          >
                            <div className="flex items-center space-x-3">
                              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                                <CalendarCheck className="w-4 h-4 text-blue-600" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">{pendingBookingCount} online booking{pendingBookingCount !== 1 ? 's' : ''} to confirm</p>
                                <p className="text-xs text-gray-500">Click to view</p>
                              </div>
                            </div>
                          </button>
                        )}
                      </>
                    ) : (
                      <div className="px-4 py-3 text-sm text-gray-500">
                        No new notifications
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* User Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center space-x-2 px-2 md:px-3 py-1.5 rounded hover:bg-gray-50 transition-colors"
              >
                <div className="w-7 h-7 md:w-6 md:h-6 bg-red-800 rounded-full flex items-center justify-center">
                  <span className="text-xs font-semibold text-white">
                    {user?.username?.charAt(0).toUpperCase() || 'U'}
                  </span>
                </div>
                <span className="text-sm text-gray-700 font-medium hidden md:inline">
                  {user?.username || 'Loading...'}
                </span>
                <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform hidden md:block ${showUserMenu ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown Menu */}
              {showUserMenu && (
                <>
                  {/* Backdrop to close menu */}
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowUserMenu(false)}
                  />

                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                    {/* Show username on mobile */}
                    <div className="px-4 py-2 border-b border-gray-200 md:hidden">
                      <p className="text-sm font-medium text-gray-900">{user?.username || 'User'}</p>
                    </div>

                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        navigate('/settings');
                      }}
                      className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <Settings className="w-4 h-4 text-gray-400" />
                      <span>Settings</span>
                    </button>

                    <div className="border-t border-gray-200 my-1"></div>

                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        handleLogout();
                      }}
                      className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Logout</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <Routes>
          <Route path="/new-booking" element={<BookingPage />} />
          <Route path="/olivia" element={<OliviaPage />} />
          <Route path="/playbook" element={<PlaybookPage />} />
          <Route path="/appointments" element={<AppointmentsPage />} />
          <Route path="/calendar" element={<CalendarPage />} />
          <Route path="/alf" element={user?.role === 'coordinator' ? <RestrictedPage /> : <AlfPage />} />
          <Route path="/appointments/:id" element={<AppointmentDetailPage />} />
          <Route path="/technicians" element={<TechniciansPage />} />
          <Route path="/technicians/:id" element={<TechnicianDetailPage />} />
          <Route path="/clients" element={<ClientsPage />} />
          <Route path="/clients/:id" element={<ClientDetailPage />} />
          <Route path="/payroll" element={user?.role === 'coordinator' ? <RestrictedPage /> : <PayrollPage />} />
          <Route path="/finance" element={user?.role === 'coordinator' ? <RestrictedPage /> : <FinancePage />} />
          <Route path="/gift-cards" element={user?.role === 'coordinator' ? <RestrictedPage /> : <GiftCardsPage />} />
          <Route path="/online-bookings" element={<OnlineBookingsPage />} />
          <Route path="/hiring" element={user?.role === 'coordinator' ? <RestrictedPage /> : <HiringPage />} />
          <Route path="/requests" element={<RequestsPage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/locations" element={<LocationsPage />} />
          <Route path="/locations/:id" element={<LocationDetailPage />} />
          <Route path="/outreach" element={user?.role === 'coordinator' ? <RestrictedPage /> : <OutreachPage />} />
          <Route path="/leads" element={<LeadsPage />} />
          <Route path="/rebook" element={<RebookPage />} />
          <Route path="/leads/:id" element={<LeadDetailPage />} />
          <Route path="/tasks" element={<TasksPage />} />
          <Route path="/comms" element={<CommsPage />} />
          <Route path="/messages" element={<MessagingPage />} />
          <Route path="/calls" element={['admin', 'operator'].includes(user?.role) ? <CallsPage /> : <RestrictedPage />} />
          <Route path="/coverage" element={<CoveragePage />} />
          <Route path="/quality" element={user?.role === 'coordinator' ? <RestrictedPage /> : <QualityPage />} />
          <Route path="/photos" element={<PhotoGalleryPage />} />
          <Route path="/google-ads" element={user?.role === 'coordinator' ? <RestrictedPage /> : <GoogleAdsPage />} />
          <Route path="/gbp" element={user?.role === 'coordinator' ? <RestrictedPage /> : <GBPPage />} />
          <Route path="/metrics" element={user?.role === 'coordinator' ? <RestrictedPage /> : <MetricsPage />} />
          <Route path="/tracking" element={user?.role === 'coordinator' ? <RestrictedPage /> : <TrackingPage />} />
          <Route path="/jobs" element={user?.role === 'coordinator' ? <RestrictedPage /> : <JobsPage />} />
          <Route path="/data-quality" element={user?.role === 'coordinator' ? <RestrictedPage /> : <DataQualityPage />} />
          <Route path="/data-quality/duplicates" element={<DedupPage />} />
          <Route path="/phone-forwarding" element={<PhoneForwardingPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/test-call" element={user?.role === 'coordinator' ? <RestrictedPage /> : <MakeCallTestPage />} />
          <Route path="/campaigns" element={user?.role === 'coordinator' ? <RestrictedPage /> : <CampaignsPage />} />
          <Route path="/subscribers" element={user?.role === 'coordinator' ? <RestrictedPage /> : <SubscribersPage />} />
          <Route path="/users" element={user?.role === 'coordinator' ? <RestrictedPage /> : <UsersPage />} />
          <Route path="/" element={user?.role === 'coordinator' ? <RebookPage /> : <Dashboard />} />
        </Routes>
      </div>

      {/* Booking Assistant */}
      <BookingAssistant
        isOpen={showBookingAssistant}
        onClose={() => setShowBookingAssistant(false)}
        apiService={apiService}
        navigate={navigate}
      />

      {/* Global Call Modal */}
      <CallModal
        isOpen={!!callTarget}
        onClose={() => setCallTarget(null)}
        phoneNumber={callTarget?.phone}
        personName={callTarget?.name}
        personId={callTarget?.personId}
        apiService={apiService}
      />
    </div>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/*" element={<AppContent />} />
      </Routes>
    </Router>
  );
}

export default App;
